import i18n from "i18next";

const translateKey = (key: string): string => {
	return (i18n.t as (translationKey: string) => string)(key);
};

const BACKEND_ERROR_MAP: Record<string, string> = {
	"Invalid email or password": "errors.invalidCredentials",
	"Invalid credentials": "errors.invalidCredentials",
	"Incorrect username or password": "errors.incorrectCredentials",
	"Could not validate credentials": "errors.unauthorized",
	"Not authenticated": "errors.unauthorized",
	"Unauthorized": "errors.unauthorized",
	"Network Error": "errors.networkError",
	"Login failed. Please check your credentials.": "errors.loginFailed",
	"Failed to load reports. Please try again.": "errors.loadReportsFailed",
	"Failed to download report. Please try again.": "errors.downloadReportFailed",
	"Failed to generate report. Please try again.": "errors.generateReportFailed",
	"Failed to load holidays for the selected dates.": "errors.loadHolidaysFailed",
	"Failed to save template": "errors.saveTemplateFailed",
	"Failed to rename template": "errors.renameTemplateFailed",
	"Failed to delete template": "errors.deleteTemplateFailed",
	"Template name is required": "errors.templateNameRequired",
	"Template not found": "errors.templateNotFound",
	"A template with this name already exists": "errors.templateAlreadyExists",
};

const extractErrorMessage = (error: unknown): string | null => {
	if (typeof error === "string") {
		return error;
	}

	if (error instanceof Error) {
		return error.message;
	}

	if (error && typeof error === "object") {
		const axiosLike = error as {
			response?: { data?: { detail?: unknown } };
			message?: string;
		};

		const detail = axiosLike.response?.data?.detail;
		if (typeof detail === "string") {
			return detail;
		}
		if (Array.isArray(detail) && detail.length > 0) {
			const first = detail[0];
			if (typeof first === "string") {
				return first;
			}
			if (first && typeof first === "object" && "msg" in first) {
				return String((first as { msg: unknown }).msg);
			}
		}

		if (typeof axiosLike.message === "string") {
			return axiosLike.message;
		}
	}

	return null;
};

export const translateErrorMessage = (
	message: string,
	fallbackKey?: string,
): string => {
	const trimmed = message.trim();
	if (!trimmed) {
		return fallbackKey ? translateKey(fallbackKey) : trimmed;
	}

	const mappedKey = BACKEND_ERROR_MAP[trimmed];
	if (mappedKey) {
		return translateKey(mappedKey);
	}

	if (/^Report with id .+ not found$/.test(trimmed)) {
		return translateKey("errors.reportNotFound");
	}

	return trimmed;
};

export const getTranslatedError = (
	error: unknown,
	fallbackKey: string,
): string => {
	const message = extractErrorMessage(error);
	if (!message) {
		return translateKey(fallbackKey);
	}

	const translated = translateErrorMessage(message);
	if (translated !== message) {
		return translated;
	}

	return message;
};
